import { Box, Button, Chip, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { arrayUnion, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from 'src/firebase/firebaseConfig';
import { Gig } from 'shared/types/appTypes';
import { useSession } from 'src/SessionContext';
import { useState } from 'react';

interface ButtonsSwitchProps {
  gig: Gig;
  onShowConfirmation?: (gig: Gig) => Promise<void>;
}

const ButtonsSwitch: React.FC<ButtonsSwitchProps> = ({ gig, onShowConfirmation }) => {
  const { status, id } = gig;
  const notifications = useNotifications();
  const { session } = useSession();
  const user = session?.user?.name;

  // State for checkboxes
  const [readProductionSheet, setReadProductionSheet] = useState(false);
  const [knowTimeAndTransport, setKnowTimeAndTransport] = useState(false);
  const [noFurtherQuestions, setNoFurtherQuestions] = useState(false);

  const ensureGigDateIsCurrent = async () => {
    if (!gig.eventId) {
      throw new Error('This gig is missing its event link. Please contact the office.');
    }

    const eventDoc = await getDoc(doc(db, 'events', gig.eventId));
    const canonicalEventDate = eventDoc.exists() ? eventDoc.data()?.dateISO8601 : null;

    if (!canonicalEventDate) {
      throw new Error('This event is missing its date. Please contact the office.');
    }

    if (gig.eventDate !== canonicalEventDate) {
      const repairEventGigDates = httpsCallable(functions, 'repairEventGigDates');
      await repairEventGigDates({ eventId: gig.eventId });
      throw new Error('This gig date was out of sync and is being repaired. Please refresh before responding.');
    }
  };

  const handleButtonClick = async (newStatus: string) => {
    if (!id) return;
    try {
      await ensureGigDateIsCurrent();

      await setDoc(
        doc(db, 'gigs', id),
        {
          status: newStatus,
          updatedAt: Timestamp.now(),
          updatedBy: user || 'Unknown',
          statusTimestamps: arrayUnion({
            changedTo: newStatus,
            changedAt: Timestamp.now(),
            changedBy: user || 'Unknown'
          })
        },
        { merge: true }
      );
      notifications.show('Status updated successfully', { severity: 'success', autoHideDuration: 3000 });
    } catch (error) {
      notifications.show('Failed to update status', { severity: 'error', autoHideDuration: 3000 });
      console.error('Error updating status:', error);
    }
  };

  const handleAvailableClick = async () => {
    // For offered statuses, show confirmation dialog first
    if ((status === 'Offered' || status === 'Offered (Travel)') && onShowConfirmation) {
      await onShowConfirmation(gig);
    } else {
      // For other statuses, proceed directly
      await handleButtonClick('Booked');
    }
  };

  switch (status) {
    case 'Check Availability':
      return (
        <>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Unavailable')} fullWidth>
              Not Available
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Available')} fullWidth>
              Available
            </Button>
          </Box>
        </>
      );
    case 'Check General Availability':
      return (
        <>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Unavailable')} fullWidth>
              Not Available
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Available')} fullWidth>
              Available
            </Button>
          </Box>
        </>
      );
    case 'Offered':
      return (
        <>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Unavailable')} fullWidth>
              Not Available
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={handleAvailableClick} fullWidth>
              Available
            </Button>
          </Box>
        </>
      );
    case 'Offered (Travel)':
      return (
        <>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={() => handleButtonClick('Unavailable')} fullWidth>
              Not Available
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Button variant="outlined" onClick={handleAvailableClick} fullWidth>
              Available
            </Button>
          </Box>
        </>
      );
    case 'Unavailable':
      return (
        <Box sx={{ flex: 1 }}>
          <Button variant="outlined" onClick={() => handleButtonClick('Available')} fullWidth>
            Never mind, mark me as available
          </Button>
        </Box>
      );
    case 'Available':
      return (
        <Box sx={{ flex: 1 }}>
          <Chip
            color="info"
            variant="outlined"
            sx={{ width: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 1 } }}
            label="You've marked yourself as available for this gig, and are expected to hold the date. If you need to cancel, contact us immediately."
          />
        </Box>
      );
    case 'Booked':
      return (
        <Box sx={{ flex: 1 }}>
          <Chip
            color="success"
            variant="outlined"
            sx={{ width: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 1 } }}
            label="You are booked for this gig. Information is preliminary and subject to change. Please check back for updates.  We look forward to working with you!"
          />
        </Box>
      );
    case 'Confirm Details':
      return (
        <Stack spacing={2} sx={{ flex: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={readProductionSheet}
                onChange={(e) => setReadProductionSheet(e.target.checked)}
              />
            }
            label="I have received and read over my production sheet linked below."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={knowTimeAndTransport}
                onChange={(e) => setKnowTimeAndTransport(e.target.checked)}
              />
            }
            label="I know what time and how I am getting to the event."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={noFurtherQuestions}
                onChange={(e) => setNoFurtherQuestions(e.target.checked)}
              />
            }
            label="I do not have any further questions."
          />
          <Button 
            variant="outlined" 
            onClick={() => handleButtonClick('Details Confirmed')} 
            fullWidth
            disabled={!readProductionSheet || !knowTimeAndTransport || !noFurtherQuestions}
          >
            I confirm I'm ready for the event
          </Button>
        </Stack>
      );
    case 'Reconfirm Details':
      return (
        <Stack spacing={2} sx={{ flex: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={readProductionSheet}
                onChange={(e) => setReadProductionSheet(e.target.checked)}
              />
            }
            label="I have received and read over my NEW production sheet linked below."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={knowTimeAndTransport}
                onChange={(e) => setKnowTimeAndTransport(e.target.checked)}
              />
            }
            label="I know what time and how I am getting to the event."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={noFurtherQuestions}
                onChange={(e) => setNoFurtherQuestions(e.target.checked)}
              />
            }
            label="I do not have any further questions."
          />
          <Button 
            variant="outlined" 
            onClick={() => handleButtonClick('Details Confirmed')} 
            fullWidth
            disabled={!readProductionSheet || !knowTimeAndTransport || !noFurtherQuestions}
          >
            I confirm I'm ready for the event
          </Button>
        </Stack>
      );
    default:
      return null;
  }
};

export default ButtonsSwitch;
